import React, { useState, useEffect } from 'react';
import { AlertTriangle, Phone, X, MapPin, Loader2, Shield, CheckCircle } from 'lucide-react';
import { Button } from './ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { toast } from 'sonner';
import api from '../services/api';

const SOSButton = ({ loadId = null, className = '' }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [alertSent, setAlertSent] = useState(false);
  const [activeAlert, setActiveAlert] = useState(null);
  const [location, setLocation] = useState(null);
  const [locationError, setLocationError] = useState(null);
  const [countdown, setCountdown] = useState(5);

  // Get location when dialog opens
  useEffect(() => {
    if (isOpen && !location) {
      getLocation();
    }
  }, [isOpen]);

  // Countdown timer for confirmation
  useEffect(() => {
    let timer;
    if (isConfirming && countdown > 0) {
      timer = setTimeout(() => setCountdown(countdown - 1), 1000);
    } else if (isConfirming && countdown === 0) {
      sendSOSAlert();
    }
    return () => clearTimeout(timer);
  }, [isConfirming, countdown]);

  const getLocation = () => {
    if (!navigator.geolocation) {
      setLocationError('Geolocation not supported');
      return;
    }

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        setLocation({ latitude, longitude });
        
        // Try to get address from coordinates (reverse geocoding)
        try {
          const response = await fetch(
            `https://nominatim.openstreetmap.org/reverse?lat=${latitude}&lon=${longitude}&format=json`
          );
          const data = await response.json();
          if (data.display_name) {
            setLocation(prev => ({ ...prev, address: data.display_name }));
          }
        } catch (e) {
          console.log('Could not get address');
        }
      },
      (error) => {
        setLocationError('Could not get location: ' + error.message);
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const startConfirmation = () => {
    setIsConfirming(true);
    setCountdown(5);
  };

  const cancelConfirmation = () => {
    setIsConfirming(false);
    setCountdown(5);
  };

  const sendSOSAlert = async () => {
    setIsSending(true);
    try {
      const alertData = {
        latitude: location?.latitude || 0,
        longitude: location?.longitude || 0,
        location_address: location?.address || 'Location unavailable',
        load_id: loadId,
        alert_type: 'sos',
        message: 'Emergency SOS activated by driver',
      };

      const response = await api.post('/sos/alert', alertData);
      
      if (response.data.success) {
        setActiveAlert(response.data.alert);
        setAlertSent(true);
        toast.success('🚨 SOS Alert Sent! Help is on the way!');
      }
    } catch (error) {
      toast.error('Failed to send SOS alert. Please call 911 directly.');
      console.error('SOS Error:', error);
    } finally {
      setIsSending(false);
      setIsConfirming(false);
    }
  };

  const cancelAlert = async () => {
    if (!activeAlert) return;
    
    try {
      await api.delete(`/sos/alerts/${activeAlert.id}/cancel`);
      setAlertSent(false);
      setActiveAlert(null);
      setIsOpen(false);
      toast.info('SOS Alert cancelled');
    } catch (error) {
      toast.error('Could not cancel alert');
    }
  };

  const call911 = () => {
    window.location.href = 'tel:911';
  };

  return (
    <>
      {/* Floating SOS Button */}
      <button
        onClick={() => setIsOpen(true)}
        className={`fixed bottom-24 right-6 z-50 w-16 h-16 bg-red-600 hover:bg-red-700 rounded-full shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 flex items-center justify-center group ${className}`}
        style={{ boxShadow: '0 6px 24px rgba(220, 38, 38, 0.5)' }}
        data-testid="sos-button"
        title="Emergency SOS"
      >
        <div className="relative">
          <Shield className="w-8 h-8 text-white" />
          <span className="absolute -top-1 -right-1 w-3 h-3 bg-white rounded-full animate-ping"></span>
        </div>
      </button>

      {/* SOS Dialog */}
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="sm:max-w-md bg-white border-red-200">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-red-600 flex items-center gap-2">
              <AlertTriangle className="w-7 h-7" />
              Emergency SOS
            </DialogTitle>
            <DialogDescription className="text-gray-600">
              {!alertSent 
                ? 'Press and hold to send an emergency alert to dispatch and call 911.'
                : 'Your SOS alert has been sent. Help is on the way.'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Location Status */}
            <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
              <MapPin className={`w-5 h-5 ${location ? 'text-green-600' : 'text-gray-400'}`} />
              <div className="flex-1">
                <p className="text-sm font-medium text-gray-900">
                  {location ? 'Location Found' : 'Getting Location...'}
                </p>
                {location?.address && (
                  <p className="text-xs text-gray-500 truncate">{location.address}</p>
                )}
                {locationError && (
                  <p className="text-xs text-red-500">{locationError}</p>
                )}
              </div>
              {location && <CheckCircle className="w-5 h-5 text-green-600" />}
            </div>

            {!alertSent ? (
              <>
                {/* Confirmation State */}
                {isConfirming ? (
                  <div className="text-center py-6">
                    <div className="relative w-24 h-24 mx-auto mb-4">
                      <div className="absolute inset-0 rounded-full border-4 border-red-200"></div>
                      <div 
                        className="absolute inset-0 rounded-full border-4 border-red-600 border-t-transparent animate-spin"
                        style={{ animationDuration: '1s' }}
                      ></div>
                      <div className="absolute inset-0 flex items-center justify-center">
                        <span className="text-3xl font-bold text-red-600">{countdown}</span>
                      </div>
                    </div>
                    <p className="text-lg font-semibold text-gray-900 mb-2">
                      Sending SOS in {countdown} seconds...
                    </p>
                    <p className="text-sm text-gray-500 mb-4">
                      Tap cancel if this was a mistake
                    </p>
                    <Button
                      variant="outline"
                      onClick={cancelConfirmation}
                      className="border-gray-300"
                    >
                      <X className="w-4 h-4 mr-2" />
                      Cancel
                    </Button>
                  </div>
                ) : (
                  <>
                    {/* Main SOS Button */}
                    <button
                      onClick={startConfirmation}
                      disabled={isSending}
                      className="w-full py-6 bg-red-600 hover:bg-red-700 text-white rounded-xl font-bold text-xl transition-all duration-200 flex items-center justify-center gap-3 disabled:opacity-50"
                      data-testid="sos-send-btn"
                    >
                      {isSending ? (
                        <>
                          <Loader2 className="w-6 h-6 animate-spin" />
                          Sending Alert...
                        </>
                      ) : (
                        <>
                          <AlertTriangle className="w-6 h-6" />
                          SEND SOS ALERT
                        </>
                      )}
                    </button>

                    {/* Call 911 Button */}
                    <button
                      onClick={call911}
                      className="w-full py-4 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-semibold text-lg transition-all duration-200 flex items-center justify-center gap-3"
                      data-testid="call-911-btn"
                    >
                      <Phone className="w-5 h-5" />
                      Call 911 Directly
                    </button>

                    <p className="text-xs text-center text-gray-500">
                      SOS Alert notifies dispatch of your location and situation.
                      <br />
                      For life-threatening emergencies, always call 911.
                    </p>
                  </>
                )}
              </>
            ) : (
              /* Alert Sent State */
              <div className="text-center py-4">
                <div className="w-20 h-20 mx-auto mb-4 bg-green-100 rounded-full flex items-center justify-center">
                  <CheckCircle className="w-10 h-10 text-green-600" />
                </div>
                <h3 className="text-xl font-bold text-green-600 mb-2">
                  Alert Sent Successfully!
                </h3>
                <p className="text-gray-600 mb-4">
                  Dispatch has been notified of your emergency.
                  <br />
                  Stay calm and stay where you are if safe.
                </p>
                
                <div className="space-y-3">
                  <button
                    onClick={call911}
                    className="w-full py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold flex items-center justify-center gap-2"
                  >
                    <Phone className="w-5 h-5" />
                    Call 911
                  </button>
                  
                  <button
                    onClick={cancelAlert}
                    className="w-full py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg font-medium flex items-center justify-center gap-2"
                  >
                    <X className="w-4 h-4" />
                    Cancel Alert (False Alarm)
                  </button>
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default SOSButton;
